/**
 * Утилиты для проверки браузера и окружения
 */

// Проверка, является ли браузер встроенным (in-app browser)
export const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // Проверяем популярные встроенные браузеры
  const inAppBrowsers = [
    /FBAN|FBAV/i,           // Facebook
    /Instagram/i,            // Instagram
    /Twitter/i,              // Twitter (X)
    /Line\//i,               // Line
    /WhatsApp/i,             // WhatsApp
    /Telegram/i,             // Telegram
    /Discord/i,              // Discord
    /VK\//i,                 // VKontakte
    /Viber/i,                // Viber
    /WeChat/i,               // WeChat
    /LinkedIn/i,             // LinkedIn
    /Snapchat/i,             // Snapchat
    /Pinterest/i,            // Pinterest
    /Reddit/i,               // Reddit (app)
    /TikTok/i,               // TikTok
    /messenger/i,            // Facebook Messenger
  ];

  return inAppBrowsers.some(pattern => pattern.test(ua));
};

// Определение конкретного встроенного браузера
export const detectInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/FBAN|FBAV/i.test(ua)) return 'Facebook';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/Twitter/i.test(ua)) return 'Twitter';
  if (/Telegram/i.test(ua)) return 'Telegram';
  if (/Discord/i.test(ua)) return 'Discord';
  if (/WhatsApp/i.test(ua)) return 'WhatsApp';
  if (/VK\//i.test(ua)) return 'VKontakte';
  if (/Viber/i.test(ua)) return 'Viber';
  if (/WeChat/i.test(ua)) return 'WeChat';
  if (/LinkedIn/i.test(ua)) return 'LinkedIn';
  if (/messenger/i.test(ua)) return 'Messenger';
  
  return 'Встроенный браузер';
};

// Проверка поддержки localStorage
export const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Проверка поддержки sessionStorage
export const isSessionStorageAvailable = () => {
  try {
    const test = '__sessionStorage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Проверка, что страница полностью загружена
export const isPageFullyLoaded = () => {
  return document.readyState === 'complete';
};

// Проверка поддержки критичных API
export const checkCriticalAPIs = () => {
  const checks = {
    localStorage: isLocalStorageAvailable(),
    sessionStorage: isSessionStorageAvailable(),
    fetch: typeof fetch === 'function',
    Promise: typeof Promise === 'function',
    clipboard: navigator.clipboard !== undefined,
    URLSearchParams: typeof URLSearchParams === 'function'
  };
  
  return checks;
};

// Проверка приватного режима браузера
export const isPrivateMode = async () => {
  try {
    // Тест для большинства браузеров
    if (!isLocalStorageAvailable()) {
      return true;
    }
    
    // Дополнительная проверка через IndexedDB
    if (window.indexedDB) {
      try {
        const db = await new Promise((resolve, reject) => {
          const request = indexedDB.open('test');
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject();
        });
        db.close();
        return false;
      } catch (e) {
        return true;
      }
    }
    
    return false;
  } catch (e) {
    return false;
  }
};

// Получить информацию о браузере
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  
  if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
    browserName = 'Chrome';
    browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Edg') > -1) {
    browserName = 'Edge';
    browserVersion = ua.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Firefox') > -1) {
    browserName = 'Firefox';
    browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    browserName = 'Safari';
    browserVersion = ua.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) {
    browserName = 'Opera';
    browserVersion = ua.match(/(?:Opera|OPR)\/(\d+)/)?.[1] || 'Unknown';
  }
  
  return { browserName, browserVersion };
};

// Комплексная проверка готовности браузера
export const performBrowserCheck = async () => {
  const isInApp = isInAppBrowser();
  const inAppName = isInApp ? detectInAppBrowser() : null;
  const localStorageOk = isLocalStorageAvailable();
  const pageLoaded = isPageFullyLoaded();
  const criticalAPIs = checkCriticalAPIs();
  const privateMode = await isPrivateMode();
  const browserInfo = getBrowserInfo();
  
  const allChecks = {
    isInAppBrowser: isInApp,
    inAppBrowserName: inAppName,
    localStorage: localStorageOk,
    pageLoaded: pageLoaded,
    apis: criticalAPIs,
    privateMode: privateMode,
    browser: browserInfo,
    isSafe: !isInApp && localStorageOk && pageLoaded && !privateMode,
    warnings: []
  };
  
  // Собираем предупреждения
  if (isInApp) {
    allChecks.warnings.push({
      level: 'error',
      message: `Вы открыли сайт во встроенном браузере ${inAppName}. История не сохранится!`,
      action: 'Откройте в обычном браузере (Chrome, Safari, Firefox)'
    });
  }
  
  if (!localStorageOk) {
    allChecks.warnings.push({
      level: 'error',
      message: 'LocalStorage недоступен. Невозможно сохранить историю!',
      action: 'Проверьте настройки браузера или отключите приватный режим'
    });
  }
  
  if (privateMode) {
    allChecks.warnings.push({
      level: 'warning',
      message: 'Вы используете приватный режим. История может не сохраниться.',
      action: 'Рекомендуем использовать обычный режим'
    });
  }
  
  if (!pageLoaded) {
    allChecks.warnings.push({
      level: 'warning',
      message: 'Страница ещё загружается. Подождите полной загрузки.',
      action: 'Дождитесь завершения загрузки'
    });
  }
  
  if (!criticalAPIs.fetch) {
    allChecks.warnings.push({
      level: 'error',
      message: 'Ваш браузер слишком старый и не поддерживается.',
      action: 'Обновите браузер до последней версии'
    });
  }
  
  return allChecks;
};

// Получить рекомендацию по браузеру
export const getBrowserRecommendation = () => {
  const platform = navigator.platform;
  const ua = navigator.userAgent;
  
  if (/iPhone|iPad|iPod/.test(ua)) {
    return {
      primary: 'Safari',
      alternatives: ['Chrome для iOS', 'Firefox для iOS'],
      note: 'Используйте Safari для лучшей совместимости с iOS'
    };
  }
  
  if (/Android/.test(ua)) {
    return {
      primary: 'Chrome',
      alternatives: ['Firefox', 'Samsung Internet'],
      note: 'Используйте Chrome для лучшей производительности'
    };
  }
  
  if (/Win/.test(platform)) {
    return {
      primary: 'Chrome',
      alternatives: ['Edge', 'Firefox'],
      note: 'Chrome и Edge обеспечивают лучшую совместимость'
    };
  }
  
  if (/Mac/.test(platform)) {
    return {
      primary: 'Safari',
      alternatives: ['Chrome', 'Firefox'],
      note: 'Safari оптимизирован для macOS'
    };
  }
  
  return {
    primary: 'Chrome',
    alternatives: ['Firefox', 'Edge'],
    note: 'Используйте современный браузер'
  };
};











